import { combineReducers, configureStore, Tuple } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import createSagaMiddleware from 'redux-saga';
import { authActions, authReducers } from '../../auth/store/authSlice';
import { galleryTagsReducers } from '../../gallery-tags/store/galleryTagsSlice';
import { leadsysPictureReducers } from '../../leadsys-pictures/store/leadsysPictureSlice';
import { settingsReducers } from '../../settings/store/settingsSlice';
import { configLoaderActions, configLoaderReducers } from '../config-loader/store/config-loader-slice';
import rootSaga from './rootSaga';

const saga = createSagaMiddleware();

const reducers = combineReducers({
  configLoader: configLoaderReducers,
  auth: authReducers,
  settings: settingsReducers,
  leadsysPicture: leadsysPictureReducers,
  galleryTags: galleryTagsReducers,
});

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  attachReduxState: true, // send the whole redux store state to Sentry

  // Transform the state to remove sensitive information
  stateTransformer: (state: RootState) => {
    return {
      ...state,
      auth: {
        ...state.auth,
        idToken: 'redacted',
        localId: 'redacted',
        refreshToken: 'redacted',
      },
      configLoader: {
        appConfig: {
          firebase: 'redacted',
        },
      },
    };
  },

  // Transform Action Payloads to remove sensitive information
  actionTransformer: (action) => {
    if (action.type === authActions.logInWithEmailAndPassword.type) {
      return {
        ...action,
        password: 'redacted',
      };
    }

    if (action.type === authActions.logInWithLocalIdSuccess.type) {
      return {
        ...action,
        sessionId: 'redacted',
      };
    }

    if (action.type === authActions.logInWithEmailAndPasswordSuccess.type) {
      return {
        ...action,
        firebaseLoginResponseDTO: 'redacted',
      };
    }

    if (action.type === authActions.logInWithLocalId.type) {
      return {
        ...action,
        password: 'redacted',
      };
    }

    if (action.type === authActions.loginToFirebaseByOAuthSuccess.type) {
      return {
        ...action,
        idToken: 'redacted',
      };
    }

    if (action.type === configLoaderActions.fetchConfigSuccess.type) {
      return {
        ...action,
        payload: {
          appConfig: {
            ...action.payload.appConfig,
            firebase: 'redacted',
          },
        },
      };
    }

    return action;
  },
});

const store = configureStore({
  reducer: reducers,
  devTools: !import.meta.env.PROD,
  // RTK 2 requires callbacks here. We deliberately run ONLY the saga middleware
  // (no default middleware) — matching the previous `middleware: [saga]`.
  middleware: () => new Tuple(saga),
  // `enhancers` must be a callback in RTK 2; `getDefaultEnhancers()` applies the
  // configured middleware, so it must be composed with the Sentry enhancer.
  enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(sentryReduxEnhancer),
});

saga.run(rootSaga);

export default store;

export type RootState = ReturnType<typeof store.getState>;
